import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
    ArrowUpRight,
    MessageCircle,
    Gamepad2,
    GraduationCap,
    Layers,
    Award,
    ScrollText,
    LockKeyhole,
    Network,
    Quote,
    ChevronDown,
    ClipboardList,
} from "lucide-react";
import Marquee from "../components/Marquee";
import PageShell from "../components/PageShell";
import {
    CARGOS,
    REGRAS_STAFF,
    REGRAS_PAGO,
    RANK_COLORS,
    DISCORD_URL,
    ROBLOX_URL,
    ESCOLA_ANNOUNCEMENT,
    ESCOLA_NOTE,
} from "../data/staffData";

const STATS = [
    { icon: Layers, value: "12", label: "Cargos na Hierarquia", id: "cargos" },
    { icon: Award, value: "12", label: "Ranks Oficiais", id: "ranks" },
    { icon: ScrollText, value: "8", label: "Regras Gerais", id: "regras" },
    { icon: LockKeyhole, value: "5", label: "Regras Staff Pago", id: "regras-pago" },
];

const QUICK_LINKS = [
    { to: "/formulario-staff", icon: ClipboardList, title: "Formulário Staff", desc: "Candidate-se para entrar na equipe", id: "formulario-staff" },
    { to: "/hierarquia", icon: Network, title: "Hierarquia", desc: "Cargos, funções, quotas e ranks", id: "hierarquia" },
    { to: "/ranks", icon: Award, title: "Ranks", desc: "Tabela completa de ranks da Staff", id: "ranks" },
    { to: "/regras", icon: ScrollText, title: "Regras Staff", desc: "8 regras gerais da equipe", id: "regras" },
    { to: "/regras-pago", icon: LockKeyhole, title: "Regras Staff Pago", desc: "5 regras específicas de cargos pagos", id: "regras-pago" },
];

export default function Inicio() {
    const heroRef = useRef(null);
    const { scrollYProgress } = useScroll({
        target: heroRef,
        offset: ["start start", "end start"],
    });
    const yOrb1 = useTransform(scrollYProgress, [0, 1], [0, 160]);
    const yOrb2 = useTransform(scrollYProgress, [0, 1], [0, -120]);

    return (
        <PageShell testid="page-inicio">
            <section
                ref={heroRef}
                data-testid="hero-section"
                className="relative -mx-4 -mt-8 overflow-hidden px-4 pb-16 pt-16 sm:-mx-6 sm:px-6 lg:-mx-10 lg:-mt-10 lg:px-10 lg:pt-24"
            >
                <div className="grid-overlay pointer-events-none absolute inset-0" />
                <motion.div
                    style={{ y: yOrb1 }}
                    className="orb pointer-events-none absolute -left-24 top-10 h-72 w-72 rounded-full bg-violet-600/25 blur-[110px]"
                />
                <motion.div
                    style={{ y: yOrb2 }}
                    className="orb pointer-events-none absolute -right-20 top-32 h-80 w-80 rounded-full bg-blue-600/20 blur-[120px]"
                />
                <div className="relative">
                    <p className="eyebrow">
                        Escola Preparatória de Staff • Comunidade Roblox
                    </p>
                    <motion.h1
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.1 }}
                        className="mt-4 font-display text-5xl font-extrabold leading-[1.02] tracking-tight sm:text-6xl lg:text-7xl"
                    >
                        <span className="block text-white">EQUIPE STAFF</span>
                        <span className="hero-gradient-text block">[EBH1]</span>
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, delay: 0.25 }}
                        className="mt-5 max-w-xl text-sm leading-relaxed text-gray-400 sm:text-base"
                    >
                        Portal informativo oficial da Escola Staff e da Comunidade Staff
                        do Roblox. Consulte a hierarquia completa, os ranks, as quotas e
                        todas as regras da equipe em um só lugar.
                    </motion.p>
                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, delay: 0.4 }}
                        className="mt-8 flex flex-wrap items-center gap-3"
                    >
                        <Link
                            data-testid="hero-cta-formulario"
                            to="/formulario-staff"
                            className="group flex items-center gap-2 rounded-full bg-gradient-to-r from-violet-600 to-indigo-600 px-6 py-3 text-sm font-bold text-white shadow-lg shadow-violet-900/40 transition-transform duration-300 hover:scale-[1.03]"
                        >
                            <ClipboardList className="h-4 w-4" /> Formulário Staff
                        </Link>
                        <Link
                            data-testid="hero-cta-hierarquia"
                            to="/hierarquia"
                            className="group flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-6 py-3 text-sm font-bold text-gray-200 transition-colors hover:bg-white/10"
                        >
                            Ver Hierarquia
                            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </Link>
                        <a
                            data-testid="hero-cta-discord"
                            href={DISCORD_URL}
                            target="_blank"
                            rel="noreferrer"
                            className="flex items-center gap-2 rounded-full border border-indigo-500/40 bg-indigo-500/10 px-6 py-3 text-sm font-bold text-indigo-300 transition-colors hover:bg-indigo-500/20"
                        >
                            <MessageCircle className="h-4 w-4" /> Entrar no Discord
                        </a>
                        <a
                            data-testid="hero-cta-roblox"
                            href={ROBLOX_URL}
                            target="_blank"
                            rel="noreferrer"
                            className="flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-6 py-3 text-sm font-bold text-gray-200 transition-colors hover:bg-white/10"
                        >
                            <Gamepad2 className="h-4 w-4" /> Grupo Roblox
                        </a>
                    </motion.div>
                </div>
                <div className="relative mt-14 flex justify-center">
                    <ChevronDown className="h-5 w-5 animate-bounce text-gray-600" />
                </div>
            </section>

            <div className="-mx-4 sm:-mx-6 lg:-mx-10">
                <Marquee items={Object.keys(RANK_COLORS)} />
            </div>

            <motion.section
                data-testid="resumo-section"
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5 }}
                className="mt-12"
            >
                <p className="eyebrow">Resumo da Staff</p>
                <h2 className="mt-2 font-display text-2xl font-bold tracking-tight text-white sm:text-3xl">
                    A equipe em números
                </h2>
                <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
                    {STATS.map((stat) => (
                        <div
                            key={stat.id}
                            data-testid={`stat-card-${stat.id}`}
                            className="glass-card glass-card-hover rounded-2xl p-5"
                        >
                            <stat.icon className="h-5 w-5 text-violet-400" />
                            <p className="mt-3 font-display text-3xl font-extrabold text-white sm:text-4xl">
                                {stat.value}
                            </p>
                            <p className="mt-1 text-xs font-medium text-gray-500 sm:text-sm">
                                {stat.label}
                            </p>
                        </div>
                    ))}
                </div>
            </motion.section>

            <motion.section
                data-testid="escola-announcement"
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5 }}
                className="mt-12"
            >
                <div className="glass-card relative overflow-hidden rounded-2xl p-6 sm:p-8">
                    <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-violet-600/15 blur-[80px]" />
                    <div className="relative">
                        <div className="flex items-center gap-3">
                            <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-violet-500/25 bg-violet-500/10 text-violet-300">
                                <GraduationCap className="h-5 w-5" />
                            </div>
                            <div>
                                <p className="eyebrow">Comunicado Oficial</p>
                                <h2 className="font-display text-xl font-bold text-white sm:text-2xl">
                                    Escola Preparatória de Staff's
                                </h2>
                            </div>
                        </div>
                        <p className="mt-5 max-w-3xl text-sm leading-relaxed text-gray-300 sm:text-base">
                            {ESCOLA_ANNOUNCEMENT}
                        </p>
                        <div className="mt-5 flex items-start gap-2.5 rounded-xl border border-amber-500/25 bg-amber-500/10 px-4 py-3">
                            <Quote className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />
                            <p className="text-sm font-medium text-amber-200">
                                {ESCOLA_NOTE}
                            </p>
                        </div>
                        <div className="mt-6 flex flex-wrap gap-3">
                            <a
                                data-testid="announcement-discord-link"
                                href={DISCORD_URL}
                                target="_blank"
                                rel="noreferrer"
                                className="flex items-center gap-2 rounded-full bg-gradient-to-r from-indigo-600 to-blue-600 px-5 py-2.5 text-sm font-bold text-white transition-transform duration-300 hover:scale-[1.03]"
                            >
                                <MessageCircle className="h-4 w-4" /> discord.gg/gz96fyqWs
                            </a>
                            <a
                                data-testid="announcement-roblox-link"
                                href={ROBLOX_URL}
                                target="_blank"
                                rel="noreferrer"
                                className="flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-5 py-2.5 text-sm font-bold text-gray-200 transition-colors hover:bg-white/10"
                            >
                                <Gamepad2 className="h-4 w-4" /> Comunidade no Roblox
                            </a>
                        </div>
                    </div>
                </div>
            </motion.section>

            <motion.section
                data-testid="quick-nav-section"
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5 }}
                className="mt-12"
            >
                <p className="eyebrow">Navegação</p>
                <h2 className="mt-2 font-display text-2xl font-bold tracking-tight text-white sm:text-3xl">
                    Explore o portal
                </h2>
                <div className="mt-6 grid gap-4 sm:grid-cols-2">
                    {QUICK_LINKS.map((link) => (
                        <div key={link.id}>
                            <Link
                                data-testid={`quick-link-${link.id}`}
                                to={link.to}
                                className="glass-card glass-card-hover group flex items-center justify-between gap-4 rounded-2xl p-5"
                            >
                                <div className="flex items-center gap-4">
                                    <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-gray-300 transition-colors group-hover:border-violet-500/30 group-hover:text-violet-300">
                                        <link.icon className="h-4.5 w-4.5" />
                                    </div>
                                    <div>
                                        <p className="font-display text-base font-bold text-white">
                                            {link.title}
                                        </p>
                                        <p className="text-xs text-gray-500">{link.desc}</p>
                                    </div>
                                </div>
                                <ArrowUpRight className="h-4 w-4 shrink-0 text-gray-600 transition-all duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-violet-400" />
                            </Link>
                        </div>
                    ))}
                </div>
            </motion.section>

            <p className="mt-14 text-center font-code text-[10px] uppercase tracking-[0.3em] text-gray-600">
                {CARGOS.length} cargos • {REGRAS_STAFF.length + REGRAS_PAGO.length} regras •
                EQUIPE STAFF [EBH1]
            </p>
        </PageShell>
    );
}
